import {
  type DataCompletenessScope,
  type MappedDataCompletenessItem,
  mapDataCompletenessResponse,
} from '@/src/components/DataCompletenessIndicator/dataCompleteness.mapper'
import { useDataCompleteness } from '@/src/hooks/useDataCompleteness.hook'
import { formatDataCompletenessTimeframe } from '@/src/utils/dataCompletenessDate.utils'
import { useMemo } from 'react'

type UseLoadPlanningDataCompletenessParams = {
  locationIds: number[]
  energyType?: string
  startDate?: string
  endDate?: string
}

type LoadPlanningDataCompletenessResult = {
  items: MappedDataCompletenessItem[]
  scope: DataCompletenessScope
  qualityPercentage: number | null | undefined
  isReady: boolean
  isLoading: boolean
  timeframe: string
}

export const useLoadPlanningDataCompleteness = ({
  locationIds,
  energyType,
  startDate = '',
  endDate = '',
}: UseLoadPlanningDataCompletenessParams): LoadPlanningDataCompletenessResult => {
  const hasRequestData = locationIds.length > 0 && !!energyType && !!startDate && !!endDate

  const { data, isFetched } = useDataCompleteness({
    locationIds,
    energyType: energyType?.toLowerCase(),
    startDate,
    endDate,
    onlyMainMeters: true,
  })

  const mappedDataCompleteness = useMemo(() => mapDataCompletenessResponse(data), [data])

  return useMemo(
    () => ({
      items: mappedDataCompleteness.items,
      scope: mappedDataCompleteness.scope,
      qualityPercentage: mappedDataCompleteness.qualityPercentage,
      isReady: hasRequestData && isFetched,
      isLoading: hasRequestData && !isFetched,
      timeframe: formatDataCompletenessTimeframe(startDate, endDate),
    }),
    [
      mappedDataCompleteness.items,
      mappedDataCompleteness.scope,
      mappedDataCompleteness.qualityPercentage,
      hasRequestData,
      isFetched,
      startDate,
      endDate,
    ]
  )
}
